// frontend/src/auth/AuthService.js

import { apiRequest } from "../utils/api";

class AuthService {
  constructor() {
    this.storageKey = "sramika_auth";
  }

  getAuth() {
    const raw = localStorage.getItem(this.storageKey);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  saveAuth(data) {
    const user = data?.user || {};
    const resolvedName =
      user.name ||
      user.displayName ||
      user.fullName ||
      user.userName ||
      user.username ||
      "";
      
    const payload = {
      token: data.token,
      phone: data.user.phone,
      role: data.user.role,
      name: resolvedName,
      lastLoginAt: new Date().toISOString()
    };

    localStorage.setItem(this.storageKey, JSON.stringify(payload));
    localStorage.setItem("userPhone", data.user.phone);
    localStorage.setItem("userRole", data.user.role);
    
    // Clear legacy just in case
    this.clearLegacyAuth();
  }

  getToken() {
    const auth = this.getAuth();
    return auth?.token || null;
  }

  getRole() {
    const auth = this.getAuth();
    return auth?.role || null;
  }

  getAdminId() {
    const auth = this.getAuth();
    if (!auth || (auth.role !== "ADMIN" && auth.role !== "SUPER_ADMIN")) {
      return null;
    }
    return auth.phone;
  }

  getOwnerId() {
    const auth = this.getAuth();
    if (!auth || auth.role !== "OWNER") return null;
    return auth.phone;
  }

  hasValidSession() {
    const auth = this.getAuth();
    if (!auth?.token) return false;
    
    // Enforce 7-day session expiry (migrated from AuthContext)
    if (auth.lastLoginAt) {
      const ageMs = Date.now() - new Date(auth.lastLoginAt).getTime();
      const sevenDays = 7 * 24 * 60 * 60 * 1000;
      if (ageMs > sevenDays) {
        return false;
      }
    }
    return true;
  }

  clearLegacyAuth() {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("phone");
    localStorage.removeItem("role");
  }

  logout() {
    const token = localStorage.getItem(this.storageKey);
    if (token) {
      // Relative /api paths never reach the gateway in production — see apiRequest.
      apiRequest("/admin/logout", { method: "POST" }).catch(() => {});
    }

    // 1. Clear auth storage (read the phone first — it is removed just below)
    const loggingOutPhone = localStorage.getItem("userPhone");
    localStorage.removeItem(this.storageKey);
    localStorage.removeItem("userPhone");
    localStorage.removeItem("userRole");

    // 1b. Clear this account's cached profile photo, and the legacy global key that
    //     used to hand one user's photo to the next account on the same browser.
    if (loggingOutPhone) localStorage.removeItem(`sramika_profile_photo:${loggingOutPhone}`);
    localStorage.removeItem("sramika_profile_photo");

    // 1c. Clear in-progress onboarding drafts. These hold a full application —
    //     licence number, address, emergency contact — under a global key with no
    //     account scoping, so leaving them behind hands the next person to sign in
    //     on this browser the previous applicant's documents.
    localStorage.removeItem("sramika_onboarding_draft");
    localStorage.removeItem("sramika_alting_onboarding_draft");

    // 1d. Clear the Add-a-Car draft id, scoped and legacy. Photo uploads write to
    //     drafts/<id>/ in S3, so an id inherited by the next account on this device
    //     sends their documents into the previous owner's prefix.
    if (loggingOutPhone) localStorage.removeItem(`sramika_addcar_draftId:${loggingOutPhone}`);
    localStorage.removeItem("sramika_addcar_draftId");

    // 2. Clear legacy
    this.clearLegacyAuth();
    
    // 3. Clear session storage
    sessionStorage.clear();
    
    // 4. Force hard reload to wipe JS heap, context, and bfcache state
    window.location.replace("/login");
  }
}

export default new AuthService();
